import type { CSSProperties, ReactNode } from 'react'
import type { ImageFrame } from '../lib/types'
import { framedImageStyle, isDefaultFrame, safeFrame, shapeOf } from '../lib/frame'

// Every image a writing shows — covers, image blocks, scenes, previews — goes
// through here, so the crop & position an author sets in the editor looks the
// same in the composer, the reader and every preview. See lib/frame.ts.

interface Props {
  src: string
  alt?: string
  frame?: ImageFrame | null
  /**
   * 'cover': the caller sizes the box (className gives it a height or an
   * aspect) and the image covers it. 'shape': a full-width image whose box
   * takes the frame's shape (its own aspect when unframed).
   */
  fit?: 'cover' | 'shape'
  /** Classes for the box: size, radius, outline… */
  className?: string
  /** Extra classes for the photo when it's unframed at its own shape ('shape' fit): a wrap covers a band on phones. */
  imgClassName?: string
  style?: CSSProperties
  /** Unframed images in a 'cover' box: CSS object-position (e.g. 'center 60%'). */
  defaultPosition?: string
  draggable?: boolean
  onLoad?: (img: HTMLImageElement) => void
  /** Drawn over the image (gradients, labels). */
  children?: ReactNode
}

export function FramedImage({
  src,
  alt = '',
  frame,
  fit = 'cover',
  className = '',
  imgClassName = '',
  style,
  defaultPosition = 'center',
  draggable,
  onLoad,
  children,
}: Props) {
  const f = safeFrame(frame)
  // The box positions its image, so it's relative — unless the caller places it absolutely.
  const box = `${/(^|\s)absolute(\s|$)/.test(className) ? '' : 'relative '}overflow-hidden ${className}`
  const load = onLoad ? (e: { currentTarget: HTMLImageElement }) => onLoad(e.currentTarget) : undefined

  // Unframed, full width: the image at its own shape, no box needed.
  if (!f && fit === 'shape') {
    return (
      <div className={box} style={style}>
        <img src={src} alt={alt} draggable={draggable} onLoad={load} className={`block h-auto w-full ${imgClassName}`} />
        {children}
      </div>
    )
  }

  const boxStyle: CSSProperties = {
    ...style,
    ...(fit === 'shape' && f ? { aspectRatio: String(shapeOf(f)), width: '100%' } : {}),
  }
  // Untouched frames render exactly as before framing existed.
  if (!f || (fit === 'cover' && isDefaultFrame(f))) {
    return (
      <div className={box} style={boxStyle}>
        <img
          src={src}
          alt={alt}
          draggable={draggable}
          onLoad={load}
          style={{ objectPosition: f ? 'center' : defaultPosition }}
          className="absolute inset-0 block size-full object-cover"
        />
        {children}
      </div>
    )
  }
  return (
    <div className={box} style={{ ...boxStyle, containerType: 'size' }}>
      <img src={src} alt={alt} draggable={draggable} onLoad={load} style={framedImageStyle(f)} className="block" />
      {children}
    </div>
  )
}
